import { Component, OnDestroy, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isNil } from 'lodash-es';
import { environment } from 'src/environments/environment';
import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { EnvironmentContextService } from './app-context.service';
import { IdleService } from './idle.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {

  isLoggedIn = false;
  isSettingsLoaded = false;
  isEnvironmentLoaded = false;
  authenticating = false;
  private readonly _destroying$ = new Subject<void>();

  get isInitialized(): boolean {
    return this.isSettingsLoaded && this.isEnvironmentLoaded;
  }

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private utils: UtilsService,
    private settingsValuesService: SettingsValuesService,
    private environmentContextService: EnvironmentContextService,
    private idle: IdleService
  ) { }

  ngOnInit(): void {
    /**
     * You can subscribe to MSAL events as shown below. For more info,
     * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
     */
    this.msalBroadcastService.inProgress$
      .pipe(
        takeUntil(this._destroying$)
      )
      .subscribe((status: InteractionStatus) => {
        if (status === InteractionStatus.Logout) {
          this.authenticating = false;
          this.stopInactivityDetector();
        } else if (status === InteractionStatus.None) {
          this.authenticating = false;
          this.setIsLoggedIn();
        } else {
          this.authenticating = true;
        }
      });
  }

  async setIsLoggedIn() {
    // TODO: handle scenario where more than one account logged in
    this.isLoggedIn = this.authService.instance.getAllAccounts().length > 0;
    if (this.isLoggedIn) {

      const settings = await this.utils.http.get(`${environment.backendUrl}api/settings`);
      this.settingsValuesService.SettingsService = settings;
      this.isSettingsLoaded = true;

      const env = await this.utils.http.get(`${environment.backendUrl}api/$environment`);
      this.environmentContextService.env = env;
      this.isEnvironmentLoaded = true;

      // if no current active account, default it to first
      if (isNil(this.authService.instance.getActiveAccount())) {
        this.authService.instance.setActiveAccount(this.authService.instance.getAllAccounts()[0]);
      }

      this.startInactivityDetector();
    }
  }

  private isEmail(email: string) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  onLoginClicked() {
    this.authService.loginRedirect();
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.stopInactivityDetector();
  }

  private startInactivityDetector() {
    const inactivityTimeout = this.settingsValuesService.$inactivityTimeout;
    if (inactivityTimeout) {
      console.log(`start watching for inactivity ${inactivityTimeout} sec`, new Date().toLocaleString());
      this.idle
        .start(inactivityTimeout)
        .subscribe(() => {
          if (this.authService.instance.getActiveAccount()) {
            console.log('inactivity detected', new Date().toLocaleString());
            this.authService.logout(
              {
                // WE NEED TO CONFIGURE LOGIN_HINT optional claim ON ID TOKEN 
                // to skip the account chooser 
                account: this.authService.instance.getActiveAccount(),
              }
            );
          } else {
            console.log('No active account because logged out from another tab. Will force reload');
            window.location.reload();
          }
        });
    }
    else {
      console.log('inactivity detector not configured');
    }
  }

  private stopInactivityDetector() {
    const inactivityTimeout = this.settingsValuesService.$inactivityTimeout;
    if (inactivityTimeout) {
      console.log('stopping inactivity detector');
      this.idle.stop();
    }
  }

}
